import { allQuestions, validateRepeatingGroupAnswer } from '@submitpro/shared';
import { useEffect, useState } from 'react';
import { ProgressBar } from '../../components/intake-form/ProgressBar';
import { QuestionRenderer } from '../../components/intake-form/QuestionRenderer';
import {
  type AppliedClassification,
  type AppliedEnrichment,
  QuickStart,
} from '../../components/intake-form/QuickStart';
import { SignatureStep } from '../../components/intake-form/SignatureStep';
import { useConversationEngine } from '../../lib/conversation-engine-hook';

// ---------------------------------------------------------------------------
// Public Intake Route — Prospect-facing guided conversation
// ---------------------------------------------------------------------------
// Full-screen, no nav, agency-branded. One question at a time.
// ---------------------------------------------------------------------------

export default function PublicIntake() {
  const engine = useConversationEngine(allQuestions, 'prospect');
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [prospectToken, setProspectToken] = useState<string | null>(null);
  const [classification, setClassification] = useState<AppliedClassification | null>(null);

  // Phase 6 — anomaly check (runs once when the conversation completes)
  type Anomaly = { title: string; message: string; severity: 'low' | 'medium' | 'high' };
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [anomalyRun, setAnomalyRun] = useState(false);

  // Phase 7 — mid-flow "anything else?"
  const [moreText, setMoreText] = useState('');
  const [moreReply, setMoreReply] = useState<string | undefined>(undefined);
  const [interpreting, setInterpreting] = useState(false);

  const current = engine.currentQuestion;
  // Local draft for the current question. The hook's `answers` is a copy, so we
  // can't mutate it in place — we hold the in-progress value here and commit it
  // to the engine on Continue. The draft is keyed to its question id: on the
  // first render after advancing, the previous question's value must never
  // reach the next question's renderer (e.g. a number flowing into a
  // multiselect crashed on .includes()).
  const [draftState, setDraftState] = useState<{ qid: string | null; value: unknown }>({
    qid: null,
    value: undefined,
  });
  const draft =
    current && draftState.qid === current.id
      ? draftState.value
      : current
        ? engine.answers[current.id]
        : undefined;
  const setDraft = (v: unknown) => setDraftState({ qid: current?.id ?? null, value: v });

  // Run a non-blocking consistency check once the prospect reaches review.
  useEffect(() => {
    if (!started || current !== null || anomalyRun) return;
    setAnomalyRun(true);
    fetch('/api/intake/anomaly-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers: engine.answers }),
    })
      .then((r) => (r.ok ? r.json() : { anomalies: [] }))
      .then((d) => setAnomalies(d.anomalies ?? []))
      .catch(() => setAnomalies([]));
  }, [started, current, anomalyRun, engine.answers]);

  async function submitMore() {
    if (moreText.trim().length < 3) return;
    setInterpreting(true);
    setMoreReply(undefined);
    try {
      const res = await fetch('/api/intake/interpret-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_answers: engine.answers, update_text: moreText }),
      });
      if (!res.ok) throw new Error('unavailable');
      const data = (await res.json()) as {
        changes: { question_id: string; to: string }[];
        new_industry_type: string | null;
        reply: string;
      };
      const prefill: Record<string, unknown> = {};
      for (const ch of data.changes) {
        // Coerce common boolean strings; otherwise pass through as text.
        prefill[ch.question_id] = ch.to === 'true' ? true : ch.to === 'false' ? false : ch.to;
      }
      if (data.new_industry_type) prefill.industry_type = data.new_industry_type;
      if (Object.keys(prefill).length > 0) engine.prefillAnswers(prefill);
      setMoreReply(data.reply);
      setMoreText('');
      setAnomalyRun(false); // re-check consistency after changes
    } catch {
      setMoreReply("We couldn't apply that just now — you can edit answers directly.");
    } finally {
      setInterpreting(false);
    }
  }

  function handleApply(c: AppliedClassification) {
    setClassification(c);
    // Setting industry_type activates the matching supplement's questions.
    if (c.industry_type) engine.prefillAnswers({ industry_type: c.industry_type });
    setStarted(true);
  }

  function handleApplyEnrichment(e: AppliedEnrichment) {
    // Map extracted field_paths → question ids, then prefill the engine.
    const idByPath = new Map(allQuestions.map((q) => [q.field_path, q.id]));
    const prefill: Record<string, unknown> = {};
    for (const [path, value] of Object.entries(e.fields)) {
      const qid = idByPath.get(path);
      if (qid) prefill[qid] = value;
    }
    if (e.industry_type) prefill.industry_type = e.industry_type;
    engine.prefillAnswers(prefill);

    if (e.industry_type || e.industry_category) {
      setClassification({
        industry_type: e.industry_type,
        industry_category: e.industry_category ?? '',
        industry_subcategory: e.industry_subcategory ?? '',
        label: '',
      });
    }
    setStarted(true);
  }

  function handleContinue() {
    if (!current) return;

    // Repeating groups validate min_rows + per-row required sub-fields.
    if (current.field_type === 'repeating_group') {
      const rows = draft ?? [];
      const groupError = validateRepeatingGroupAnswer(current, rows);
      if (groupError) {
        setError(groupError);
        return;
      }
      setError(undefined);
      engine.answer(rows);
      return;
    }

    // Validate required
    if (current.required && (draft === undefined || draft === '' || draft === null)) {
      setError('This field is required');
      return;
    }

    setError(undefined);
    engine.answer(draft);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const body = classification
        ? {
            ...engine.submissionData,
            industry: {
              type: classification.industry_type,
              category: classification.industry_category,
              subcategory: classification.industry_subcategory,
            },
          }
        : engine.submissionData;
      const res = await fetch('/api/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Submission failed');
      const data = (await res.json()) as { prospectToken?: string };
      // Move to the review-&-sign step (falls back to thank-you if no token).
      if (data.prospectToken) setProspectToken(data.prospectToken);
      else setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Header bar with agency branding */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 shrink-0 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700" />
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">Centered Partners</div>
              <div className="text-xs text-slate-500 truncate">Commercial Insurance Submission</div>
            </div>
          </div>
          <div className="text-sm text-slate-500 shrink-0">
            <span className="hidden sm:inline">Need help? </span>
            <a href="tel:+13145551234" className="text-blue-600 hover:text-blue-700 font-medium">
              Call us
            </a>
          </div>
        </div>
      </header>

      {submitted ? (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <svg
                aria-hidden="true"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Thank you!</h2>
            <p className="text-slate-600">
              Your submission has been received. A member of the Centered Partners team will review
              it and reach out shortly to start shopping the market for your coverage.
            </p>
            {prospectToken && (
              <p className="mt-4">
                <a
                  href={`/api/intake/${prospectToken}/application.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 font-medium underline"
                >
                  Download your signed application (PDF)
                </a>
              </p>
            )}
          </div>
        </main>
      ) : prospectToken ? (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <SignatureStep token={prospectToken} onSigned={() => setSubmitted(true)} />
        </main>
      ) : !started ? (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <QuickStart
            onApply={handleApply}
            onApplyEnrichment={handleApplyEnrichment}
            onSkip={() => setStarted(true)}
          />
        </main>
      ) : (
        <>
          {/* Progress bar */}
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
            <ProgressBar
              percentage={engine.progress.percentage}
              sectionLabel={current?.section.replace('_', ' ')}
            />
          </div>

          {/* Question area */}
          <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
            {current ? (
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 md:p-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <QuestionRenderer
                  key={current.id}
                  question={current}
                  value={draft}
                  onChange={(v) => {
                    setError(undefined);
                    setDraft(v);
                  }}
                  error={error}
                />

                <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
                  <button
                    type="button"
                    onClick={engine.back}
                    className="text-slate-600 hover:text-slate-900 text-sm font-medium"
                  >
                    ← Back
                  </button>

                  <button
                    type="button"
                    onClick={handleContinue}
                    className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 font-medium transition shadow-sm"
                  >
                    Continue →
                  </button>
                </div>
              </div>
            ) : (
              // Review & submit
              <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 md:p-10 text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
                  <svg
                    aria-hidden="true"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Ready to submit</h2>
                <p className="text-slate-600 mb-6">
                  We've collected everything we need to get started shopping the market for your
                  coverage.
                </p>

                {/* Phase 6 — non-blocking consistency double-checks */}
                {anomalies.length > 0 && (
                  <div className="text-left mb-6 space-y-3">
                    {anomalies.map((a) => (
                      <div
                        key={a.title}
                        className="rounded-xl border border-amber-200 bg-amber-50/70 p-4"
                      >
                        <div className="text-sm font-semibold text-amber-900">🤔 {a.title}</div>
                        <p className="mt-1 text-sm text-amber-800">{a.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Phase 7 — mid-flow free-text update */}
                <div className="text-left mb-6">
                  <label
                    className="block text-sm font-medium text-slate-700 mb-2"
                    htmlFor="anything-else"
                  >
                    Anything else we should know? (optional)
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      id="anything-else"
                      type="text"
                      value={moreText}
                      onChange={(e) => setMoreText(e.target.value)}
                      placeholder="e.g. we also do HVAC and electrical work"
                      className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                    <button
                      type="button"
                      onClick={submitMore}
                      disabled={interpreting}
                      className="w-full sm:w-auto rounded-lg border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-50 px-5 py-2.5 font-medium transition whitespace-nowrap"
                    >
                      {interpreting ? 'Updating…' : 'Update'}
                    </button>
                  </div>
                  {moreReply && <p className="mt-2 text-sm text-emerald-700">{moreReply}</p>}
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white px-8 py-3 font-medium transition shadow-sm"
                >
                  {submitting ? 'Submitting...' : 'Submit my information'}
                </button>
              </div>
            )}
          </main>
        </>
      )}

      <footer className="max-w-3xl mx-auto px-4 sm:px-6 py-8 text-center text-sm text-slate-500">
        <p>
          Your information is encrypted and only used to prepare insurance quotes on your behalf.
        </p>
        <p className="mt-2 text-xs text-slate-400">
          © {new Date().getFullYear()} Kleva Ideas. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
